import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {DeleteCellComponent} from "@irb-common/components/grid/renderers/delete.cell.component";
import {DeleteHeaderComponent} from "@irb-common/components/grid/renderers/delete.header.component";
import {TruncateTooltipCellComponent} from '@irb-common/components/grid/renderers/truncatetooltip.cell.component';
import {UtilityService} from "@irb-common/services/utility.service";
import {SecurityAllocationsComponent} from "./security-allocations.component";
import {CustomfilterComponent} from "@irb/irb-clients/portfolios/customfilter/customfilter.component";
import {IrbAgSetColumnFilterComponent} from "@irb/irb-common/components/irebal-ag-grid-ent-avatar/irb-ag-set-column-filter/irb-ag-set-filter.component";
import {NumberTextBoxRendererComponent} from '@irb-common/components/grid/renderers/number-text-box.cell.component';
import { SecurityMasterAssetCategoryService } from '@irb/irb-models/security-master/security-master.asset-category.service';
import { DropdownSearchMultipleRendererComponent } from '@irb-common/components/grid/renderers/dropdown-search-multiple.cell.component';
import { SacSecurityTypeService } from "@irb/irb-models/security-master/sac-security-type.service";
@Injectable({
    providedIn: "root"
})
export class SecurityAllocationsGridLayoutService implements GridLayoutService<SecurityAllocationsComponent> {
    constructor(private util: UtilityService, private smac: SecurityMasterAssetCategoryService, private ssts: SacSecurityTypeService
    ) {
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent,
            "customFilterToolPanel": CustomfilterComponent,
            "irbAgSetColumnFilterComponent": IrbAgSetColumnFilterComponent,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent,
            DropdownSearchMultipleRendererComponent: DropdownSearchMultipleRendererComponent
        };
    }

    getColumnDefs(parent: SecurityAllocationsComponent) {
        let cols: any = [
            {
                headerName: "Delete All",
                headerComponent: "DeleteHeaderComponent",
                headerComponentParams: { 
                    message: "Remove all allocations from the model?", 
                    note: "",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: parent.isSubscribeModel
                },
                cellRenderer : "DeleteCellComponent",
                cellRendererParams: { 
                    message: "Remove this allocation from the model?",
                    note: "",
                    proceed: parent.delete.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: parent.disableDelete.bind(parent)
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
                pinned: "left",
                suppressColumnsToolPanel: true,
                lockPosition: true
            },
            {
                headerName: "Securities",
                pinned: "left",
                children: [
                    {
                        headerName: "Symbol",            
                        field: "securitySymbol",
                        sort: "asc",
                        comparator: this.util.sortString.bind(parent),
                        cellRenderer : "cellRendererFormatter",
                        cellRendererParams: {
                            link: (params: any) => {
                                const retValue = params.value && !params.data.useSecurityValue;
                                return retValue;
                            },
                            onClick: (function(data: any) {
                                parent.openEditSecuritySlide(data);
                            }).bind(parent)
                        },
                        lockVisible: true,
                        lockPosition: true,
                        minWidth: 70,
                        flex: 1
                    },
                    {
                        headerName: "Description",
                        field: "securityDescription",
                        minWidth: 180,
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter'
                    },
                    {
                        headerName: parent.ENABLE_AS_SECTYPE ? 'Trade Type' : 'Type',
                        field: "securityType",
                        minWidth: 80,
                        flex: 1,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererSelector: this.formatSecurityTypeSelector
                        
                    },
                    {
                        headerName: "CustCat",
                        field: "customCategoryName",
                        minWidth: 130,
                        flex: 2,
                        cellRenderer: 'colorCategoryComponent',
                        colId: "customCategoryName"
                    },
                    {
                        headerName: "NER",
                        field: "netExpenseRatio",
                        minWidth: 70,
                        flex: 1,
                        hide : true,
                        cellRendererSelector: (params: any) => {
                                if (params.data) {
                                  if (params.data.netExpenseRatio === null || params.data.netExpenseRatio === 0) {
                                    return {
                                        component: 'cellRendererFormatter',
                                        params: { value: 'n/a' },
                                      };
                                  } else {
                                      return{
                                        component: 'cellRendererFormatter',
                                        params: { value:  params.data.netExpenseRatio + " %"} 
                                      } 
                                  } 
                          }
                        }
                    },

                    {
                        headerName: "Target %",
                        field: "targetPercentage",
                        headerClass: 'editHeaderModelPercent',
                        lockVisible: true,
                        minWidth: 50,
                        flex: 1,
                        type: 'numericColumn',
                        cellClass: 'numberTextBoxCell',
                        cellRenderer: 'NumberTextBoxRendererComponent',
                        cellRendererParams: () => {
                            return {
                                min: 0,
                                max: 100,
                                places: 1,
                                disabled: parent.isSubscribeModel,
                                roundIfInvalid: true,
                                onChange: (params: any, c: any) => {
                                    if (params && params.data.targetPercentage != Number(c.value) && c.isValid) {
                                        params.data.targetPercentage = Number(c.value);
                                        parent.updateRow(params, true);
                                    }
                                }
                            }
                        }
                    }
                ]
            }];

                cols.push({
                    headerName: 'Schwab Asset Classes',
                    children: [
                        {
                            headerName: "Level 1",
                            field: "acLevel1",
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel1),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 1",
                                    hiddenLabelDescription: "classes"
                                };
                            }
                        },
                        {
                            headerName: "Level 2",
                            field: "acLevel2",
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel2),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 2",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel2', true)
                        },
                        {
                            headerName: "Level 3",
                            field: "acLevel3",
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel3),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 3",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel3', true)
                        },
                        {
                            headerName: "Level 4",
                            field: "acLevel4",
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel4),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 4",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel4', true)
                        }
                    ]
                });

            cols.push({
                headerName: "Rebalance Bands",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.rebalanceBands()
                },
                children: [
                    {
                        headerName: "Use Default Settings",
                        headerComponentParams: {
                            irbColInfo: this.useDefault()
                        },
                        field: "useGlobalValue",
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'checkbox', isCheckboxDisabled: false},
                        lockVisible: true,
                        minWidth: 60,
                        flex: 1
                    },
                    {
                        headerName: "Min %",
                        field: "toleranceMinPercentage",
                        editable:  (params: any) => {
                            return !params.data.useGlobalValue;
                        },
                        minWidth: 70,
                        flex: 1,
                        valueFormatter: (params: any) => {
                            return Number(params.value).toFixed(2);
                        },
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        cellClass: (params: any) => {
                            return (!params.data.useGlobalValue) ? 'numberTextBoxEditorCell' : 'alignRight';
                        },
                        cellEditor: 'NumericEditorComponent',
                        cellEditorParams: {
                            validationFn: (value: any, params: any) => {
                                const invalidMessage = 'Rebalance bands can have up to 2 decimal places';
                                const rangeMessage = 'The min % must be < or = to the target %';
                                const target = params.data.targetPercentage;
                                value = !value ? '0' : value;
                                const numValue = parseFloat(value);
                                if (!(numValue <= target)) {
                                    return rangeMessage;
                                }
                                const arrayWithDecimal = numValue.toString().split(".");
                                if (arrayWithDecimal.length > 1 && arrayWithDecimal[1].length > 2) {
                                    return invalidMessage;
                                }
                                return true;
                            },
                            // tooltiptext: 'The min % must be < or = to the target %',
                            asEditorWithBorder: true
                        }
                    },
                    {
                        headerName: "Max %",
                        field: "toleranceMaxPercentage", 
                        editable:  (params: any) => {
                            return !params.data.useGlobalValue;
                        } ,
                        valueFormatter: (params: any) => {
                            return Number(params.value).toFixed(2);
                        },
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        cellClass: (params: any) => {
                            return (!params.data.useGlobalValue) ? 'numberTextBoxEditorCell' : 'alignRight';
                        },
                        minWidth: 70,
                        flex: 1,
                        cellEditor: 'NumericEditorComponent',
                        cellEditorParams: {
                            validationFn: (value: any, params: any) => {
                                const invalidMessage = 'Rebalance bands can have up to 2 decimal places';
                                const rangeMessage = 'The max % must be > or = to the target %';
                                const target = params.data.targetPercentage;
                                const numValue = parseFloat(value);
                                if (!(numValue >= target && numValue <= 100)) {
                                    return rangeMessage;
                                }
                                const arrayWithDecimal = numValue.toString().split(".");
                                if (arrayWithDecimal.length > 1 && arrayWithDecimal[1].length > 2) {
                                    return invalidMessage;
                                }
                                return true;
                            },
                            // tooltiptext: 'The max % must be > or = to the target %',
                            asEditorWithBorder: true
                        }
                    }
                ]
            },
            {
                headerName: "Trading Rules",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.tradeRules()
                },
                children: [
                    {
                        headerName: "Use Sec Master",
                        headerComponentParams: {
                            irbColInfo: this.useSecMaster()
                        },
                        field: "useSecurityValue",
                        lockVisible: true,
                        minWidth: 70,
                        flex: 1,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'checkbox', isCheckboxDisabled: false}
                    }
                ]
            }
        );
        if (parent.ENABLE_AS_SECTYPE) {
            cols[1].children.splice(3, 0, {
                headerName: 'Security Type',
                colId: 'sacSecurityType',
                field: 'sacSecurityType',
                minWidth: 80,
                flex: 1,
                filter: 'irbAgSearchMultipleColumnFilterComponent',
                suppressFiltersToolPanel: true,
                floatingFilterComponent: 'irbInputFloatingFilterComponent',
                filterParams: (_params: any) => {
                    return {
                        values: this.ssts.getDropdownOptionsForSacSecurityTypes(),
                        enableHiddenSearchResults: false,
                        enableEmptySearchTerm: true
                    };
                }
            });

            cols[1].children.splice(7, 0, {
                headerName: "Morningstar Stock Style",
                field: 'styleBoxLabel',
                flex: 1
            });
        }

        return cols;
    }

    getTradingRulesColumnDefs(parent: SecurityAllocationsComponent) {
        let cols: any = [{
            headerName: "Delete All",
            headerComponent: "DeleteHeaderComponent",
            headerComponentParams: { 
                message: "Remove all allocations from the model?", 
                note: "",
                proceed: parent.deleteAll.bind(parent),
                cancel: parent.cancelDelete.bind(parent),
                disableDelete: parent.isSubscribeModel
            },
            cellRenderer : "DeleteCellComponent",
            cellRendererParams: { 
                message: "Remove this allocation from the model?",
                note: "",
                proceed: parent.delete.bind(parent),
                cancel: parent.cancelDelete.bind(parent),
                disableDelete: parent.disableDelete.bind(parent)
            },
            resizable: false,
            minWidth: 25,
            maxWidth: 25,
            width: 25,
            pinned: "left",
            suppressColumnsToolPanel: true,
            lockPosition: true
        },
        {
            headerName: "Securities",
            pinned: "left",
            children: [
                {
                    headerName: "Symbol",            
                    field: "securitySymbol",
                    sort: "asc",
                    comparator: this.util.sortString.bind(parent),
                    cellRenderer : "cellRendererFormatter",
                    cellRendererParams: {
                        link: (params: any) => {
                            const retValue = params.value && !params.data.useSecurityValue;
                            return retValue;
                        },
                        onClick: (function(data: any) {
                            parent.openEditSecuritySlide(data);
                        }).bind(parent)
                    },
                    lockVisible: true,
                    lockPosition: true,
                    minWidth: 70,
                    flex: 1
                },
                {
                    headerName: "Description",
                    field: "securityDescription",
                    minWidth: 180,
                    flex: 1,
                    cellRenderer: 'cellRendererFormatter'
                },
                {
                    headerName: "Type",
                    field: "securityType",
                    minWidth: 80,
                    flex: 1,
                    cellRenderer: 'cellRendererFormatter'
                    
                },
                {
                    headerName: "CustCat",
                    field: "customCategoryName",
                    minWidth: 130,
                    flex: 1,
                    cellRenderer: 'colorCategoryComponent',
                    colId: "customCategoryName"
                },
                {
                    headerName: "NER",
                    field: "netExpenseRatio",
                    headerClass: 'editHeaderModelPercent',
                    minWidth: 50,
                    flex: 1,
                    hide : true,
                    suppressFiltersToolPanel: true,
                    cellRendererSelector: (params: any) => {
                            if (params.data) {
                              if (params.data.netExpenseRatio === null || params.data.netExpenseRatio === 0) {
                                return {
                                    component: 'cellRendererFormatter',
                                    params: { value: 'n/a' },
                                  };
                              } else {
                                  return{
                                    component: 'cellRendererFormatter',
                                    params: { value:  params.data.netExpenseRatio + " %"} 
                                  } 
                              } 
                      }
                    }
                },
                {
                    headerName: "Target %",
                    field: "targetPercentage",
                    headerClass: 'editHeaderModelPercent',
                    lockVisible: true,
                    minWidth: 80,
                    flex: 1,
                    type: 'numericColumn',
                    cellClass: 'numberTextBoxCell',
                    cellRenderer: 'NumberTextBoxRendererComponent',
                    cellRendererParams: () => {
                        return {
                            min: 0,
                            max: 100,
                            places: 1,
                            disabled: parent.isSubscribeModel,
                            roundIfInvalid: true,
                            onChange: (params: any, c: any) => {
                                if (params && params.data.targetPercentage !== Number(c.value) && c.isValid) {
                                    params.data.targetPercentage = Number(c.value);
                                    parent.updateRow(params, true);
                                }
                            }
                        }
                    }
                }
            ]
        }];

            cols.push({
                headerName: 'Schwab Asset Classes',
                children: [
                    {
                        headerName: "Level 1",
                        field: "acLevel1",
                        minWidth: 160,
                        flex: 1,
                        suppressFiltersToolPanel: true,
                        sortable: false,
                        cellClass: 'dropdownSearchMultipleCell',
                        cellRenderer: 'DropdownSearchMultipleRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel1),
                                noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                cellStyle: "classes",
                                canEdit: false,
                                hiddenLabelPanelTitle: "Schwab Asset Classes - Level 1",
                                hiddenLabelDescription: "classes"
                            };
                        }
                    },
                    {
                        headerName: "Level 2",
                        field: "acLevel2",
                        minWidth: 160,
                        flex: 1,
                        suppressFiltersToolPanel: true,
                        sortable: false,
                        cellClass: 'dropdownSearchMultipleCell',
                        cellRenderer: 'DropdownSearchMultipleRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel2),
                                noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                cellStyle: "classes",
                                canEdit: false,
                                hiddenLabelPanelTitle: "Schwab Asset Classes - Level 2",
                                hiddenLabelDescription: "classes"
                            };
                        },
                        hide: this.getDefaultHiddenState(parent, 'acLevel2', true)
                    },
                    {
                        headerName: "Level 3",
                        field: "acLevel3",
                        minWidth: 160,
                        flex: 1,
                        suppressFiltersToolPanel: true,
                        sortable: false,
                        cellClass: 'dropdownSearchMultipleCell',
                        cellRenderer: 'DropdownSearchMultipleRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel3),
                                noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                cellStyle: "classes",
                                canEdit: false,
                                hiddenLabelPanelTitle: "Schwab Asset Classes - Level 3",
                                hiddenLabelDescription: "classes"
                            };
                        },
                        hide: this.getDefaultHiddenState(parent, 'acLevel3', true)
                    },
                    {
                        headerName: "Level 4",
                        field: "acLevel4",
                        minWidth: 160,
                        flex: 1,
                        suppressFiltersToolPanel: true,
                        sortable: false,
                        cellClass: 'dropdownSearchMultipleCell',
                        cellRenderer: 'DropdownSearchMultipleRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel4),
                                noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                cellStyle: "classes",
                                canEdit: false,
                                hiddenLabelPanelTitle: "Schwab Asset Classes - Level 4",
                                hiddenLabelDescription: "classes"
                            };
                        },
                        hide: this.getDefaultHiddenState(parent, 'acLevel4', true)
                    }
                ]
            });

        cols.push({
            headerName: "Rebalance Bands",
            headerGroupComponent: 'customHeaderGroup',
            headerGroupComponentParams: {
                irbColInfo: this.rebalanceBands()
            },
            children: [
                {
                    headerName: "Use Default Settings",
                    headerComponentParams: {
                        irbColInfo: this.useDefault()
                    },
                    field: "useGlobalValue",
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: false},
                    lockVisible: true,
                    minWidth: 60,
                    flex: 1
                },
                {
                    headerName: "Min %",
                    field: "toleranceMinPercentage",
                    editable:  (params: any) => {
                        return !params.data.useGlobalValue;
                    },
                    minWidth: 70,
                    flex: 1,
                    valueFormatter: (params: any) => {
                        return Number(params.value).toFixed(2);
                    },
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    cellClass: (params: any) => {
                        return (!params.data.useGlobalValue) ? 'numberTextBoxEditorCell' : 'alignRight';
                    },
                    cellEditor: 'NumericEditorComponent',
                    cellEditorParams: {
                        validationFn: (value: any, params: any) => {
                            const invalidMessage = 'Rebalance bands can have up to 2 decimal places';
                            const rangeMessage = 'The min % must be < or = to the target %';
                            const target = params.data.targetPercentage;
                            value = !value ? '0' : value;
                            const numValue = parseFloat(value);
                            if (!(numValue <= target)) {
                                return rangeMessage;
                            }
                            const arrayWithDecimal = numValue.toString().split(".");
                            if (arrayWithDecimal.length > 1 && arrayWithDecimal[1].length > 2) {
                                return invalidMessage;
                            }
                            return true;
                        },
                        // tooltiptext: 'The min % must be < or = to the target %',
                        asEditorWithBorder: true
                    }
                },
                {
                    headerName: "Max %",
                    field: "toleranceMaxPercentage", 
                    editable:  (params: any) => {
                        return !params.data.useGlobalValue;
                      } ,
                    valueFormatter: (params: any) => {
                        return Number(params.value).toFixed(2);
                    },
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    cellClass: (params: any) => {
                        return (!params.data.useGlobalValue) ? 'numberTextBoxEditorCell' : 'alignRight';
                    },
                    minWidth: 70,
                    flex: 1,
                    cellEditor: 'NumericEditorComponent',
                    cellEditorParams: {
                        validationFn: (value: any, params: any) => {
                            const invalidMessage = 'Rebalance bands can have up to 2 decimal places';
                            const rangeMessage = 'The max % must be > or = to the target %';
                            const target = params.data.targetPercentage;
                            const numValue = parseFloat(value);
                            if (!(numValue >= target && numValue <= 100)) {
                                return rangeMessage;
                            }
                            const arrayWithDecimal = numValue.toString().split(".");
                            if (arrayWithDecimal.length > 1 && arrayWithDecimal[1].length > 2) {
                                return invalidMessage;
                            }
                            return true;
                        },
                        // tooltiptext: 'The max % must be > or = to the target %',
                        asEditorWithBorder: true
                    }
                }
            ]
        },
        {
            headerName: "Trading Rules",
            headerGroupComponent: 'customHeaderGroup',
            headerGroupComponentParams: {
                irbColInfo: this.tradeRules()
            },
            children: [
                {
                    headerName: "Use Sec Master",
                    headerComponentParams: {
                        irbColInfo: this.useSecMaster()
                    },
                    field: "useSecurityValue",
                    lockVisible: true,
                    minWidth: 70,
                    flex: 1,
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: false}
                },
                {
                    headerName: "Min Buy $",
                    field: "buyMinimum",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 90,
                    flex: 1,
                    valueGetter: (params: any) => {
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.buyMinimum;
                    }
                },
                {
                    headerName: "Min Sell $",
                    field: "sellMinimum",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 90,
                    flex: 1,
                    valueGetter: (params: any) => {
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.sellMinimum;
                    }
                },
                {
                    headerName: "Min Buy %",
                    field: "buyMinimumPercent",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 70,
                    flex: 1,
                    valueGetter: (params: any) => {
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.buyMinimumPercent;
                    }
                },
                {
                    headerName: "Min Sell %",
                    field: "sellMinimumPercent",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 70,
                    flex: 1,
                    valueGetter: (params: any) => {
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.sellMinimumPercent;
                    }
                }
            ]
        },
        {
            headerName: "Restrictions",
            headerGroupComponent: 'customHeaderGroup',
            headerGroupComponentParams: {
                irbColInfo: this.restrictions()
            },
            children: [
                {
                    headerName: "Restriction Type",
                    field: "sellingTreatment",
                    cellRenderer: 'cellRendererFormatter',
                    minWidth: 120,
                    flex: 1,
                    valueGetter: (params: any) => {
                        let formatRestriction = (item: string) => {
                            switch(item) {
                                case "NEVER_SELL": return "Never Sell";
                                case "NO_RESTRICTION": return "No restriction";
                                case "NEVER_BUY": return "Never Buy";
                                case "NEVER_BUY_AND_SELL": return "Never Buy and Never Sell";
                                case "UN_MANAGED": return "Exclude";
                                default : return  "";
                            }
                        };
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : formatRestriction(params.data.sellingTreatment);
                    }
                },
                {
                    headerName: "Action to Take",
                    field: "conditions",
                    minWidth: 95,
                    flex: 1,
                    cellRenderer : "cellRendererFormatter",
                    valueGetter: (params: any) => {
                        let retValue = params.data.conditions || 'n/a';
                        if (retValue === 'Buy an alternate security') {
                            const restType = params.data.sellingTreatment;
                            const naCase = (params.data.securitySymbol === "$CASH" || (restType !== "NEVER_BUY" && restType !== "NEVER_BUY_AND_SELL"));
                            retValue = naCase ? 'n/a' : retValue;
                        }
                        return retValue;
                    }
                }
            ]
        },
        {
            headerName: "Equivalents & Alternatives",
            headerGroupComponent: 'customHeaderGroup',
            headerGroupComponentParams: {
                irbColInfo: this.equivalentsAndAlt()
            },
            children: [
                {
                    headerName: "# Equivs",
                    field: "equivalenceQty",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 80,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.equivs()
                    }
                },
                {
                    headerName: "Alt if Held",
                    field: "altIfHeldSymbol",
                    minWidth: 100,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.altAfHeld()
                    },
                    cellRenderer : "cellRendererFormatter",
                    valueGetter: (params: any) => {
                        let retVal = params.data.altIfHeldSymbol ? params.data.altIfHeldSymbol : 'none';
                        if (retVal === 'None') {
                            retVal = 'none';
                        }
                        return retVal;
                    }
                },
                {
                    headerName: "Min Alt",
                    field: "tradeMinAltName",
                    minWidth: 100,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.minAlt()
                    },
                    valueGetter: (params: any) => {
                        return params.data.tradeMinAltName ? params.data.tradeMinAltName : 'none';
                    }
                },
                {
                    headerName: "Alt Sec",
                    field: "taxAltSymbolName",
                    minWidth: 100,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.altSec()
                    },
                    cellRenderer : "cellRendererFormatter",
                    valueGetter: (params: any) => {
                        return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.taxAltSymbolName ? params.data.taxAltSymbolName : 'none';
                    }
                },
                {
                    headerName: "Alt Acct",
                    field: "inverseAccType",
                    minWidth: 100,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.altAcct()
                    },
                    valueGetter: (params: any) => {
                        return params.data.inverseAccType ? params.data.inverseAccType : 'n/a';
                    }
                },
                {
                    headerName: 'Tax Loss Harvest',
                    field: 'isTlhEnabled',
                    minWidth:85,
                    valueGetter: (params: any) => {
                        let returnValue = '';
                        switch(params.data.isTlhEnabled) {
                            case true:
                                returnValue = 'On';
                                break;
                            case false:
                                returnValue = 'Off'
                                break;
                            default:
                                returnValue = ''
                        }
                        returnValue = !parent.isTLHSetInGlobal ? '' : returnValue;
                        if (params.data && params.data.securitySymbol === "$CASH")
                          returnValue = 'n/a';
                        return returnValue;
                    },
                },
                {
                    headerName: "Wash sale alternate",
                    field: "tlhWashSaleSymbol",
                    minWidth: 100,
                    headerComponentParams: {
                        irbColInfo: this.tlhWashSaleAlternate()
                    },
                    cellRenderer : "cellRendererFormatter",
                    valueGetter: (params: any) => {
                        let retVal = params.data.tlhWashSaleSymbol ? params.data.tlhWashSaleSymbol : 'none';
                        if (retVal === 'None') {
                            retVal = 'none';
                        }
                        if (params.data && params.data.securitySymbol === "$CASH")
                          retVal = 'n/a';
                        return retVal;
                    }
                }
            ]
        },
        {
            headerName: "Household Location Prefs",
            children: [
                {
                    headerName: "Level",
                    field: "taxabilityBias",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 70,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.locationLevel()
                    }
                },
                {
                    headerName: "Roth Level",
                    field: "rothLevel",
                    type: 'numericColumn',
                    headerClass: 'not-right-align',
                    minWidth: 90,
                    flex: 1,
                    headerComponentParams: {
                        irbColInfo: this.locationRothLevel()
                    },
                }
            ]
        });

    if (!parent.isTlhWsaEnabled) {
        cols[5].children.splice(5, 2);
    }

    return cols;
    }


 // target % and Target Min %
    calWeightedTarget(modelTarget: number, securityTarget: number) {
        if (modelTarget != null && securityTarget != null) {
            let a = Number(modelTarget) * Number(securityTarget);
            let b = a / 100;

            //let rounded = Math.floor((b * 10) / 10);

            return b;
        } else {
            return 0;
        }
    }

    formatSecurityTypeSelector = (params:any) => {
        if (params.data) {
            return {
                component: 'cellRendererFormatter',
                params: {
                    value: this.formatSecurityType(params)
                }
            };
        }
    }
    formatSecurityType = (params:any) => {
        switch (params.data?.securityType) {
            case "EQUITY":
                return "Equity";
            case "EQ":
                return "Equity";
            case "MUTUAL_FUND":
                return "Mutual Fund";
            case "MF":
                return "Mutual Fund";
            case "CASH":
                return "Cash";
            case "GROUP":
                return "Group";
            case "BOND":
                return "Bond";
            case "OTHER":
                return "Other";
            case "EXTERNAL":
                return "External";
            case "CustCat":
                return "Category";
            case "OPTION":
                return "Option";
            default:
                return params.data?.securityType;
        }
    }

    useDefault() {
        return 'When this is checked, the underlying security model is following the global settings for rebalancing bands. When it\'s not checked, the rebalancing bands were overridden within the security model.';
    }
    rebalanceBands() {
        let msg= "<p>The minimum and maximum rebalancing band is automatically calculated for each model security using the values set in your global settings.</p>";
        msg += "<p>You can override the calculated bands for any security by deselecting the use default settings checkbox and entering new values.</p>";
        return msg;
    }
    tradeRules() {
        let msg = '<p><b>Min Buy</b> and <b>Min Sell</b> are your preferred minimum trade amounts for any proposed trades of the security. </p>';

        msg += '<p><b>Note:</b>&nbsp;For mutual funds, if you set both a dollar and a percent minimum, they will be compared to the applicable fund minimums, ' +
            'and the highest of these values will take precedence.</p>';
        return msg;
    }
    useSecMaster() {
        let msg = '<p>When this is checked, all usage rules will follow your global settings. When it\'s not checked, those global settings will be overridden with the values you enter here.<span id="useSecMasterInfoVideoHldr"></span></p>';
        return msg;
    }
    restrictions() {
        return '<p>A security <b>restriction</b> is a trading rule that will be applied to all portfolios following this model unless it\'s overridden within the individual portfolio.</p>';
    }
    equivalentsAndAlt() {
        const msg = "<p>The <b>Asset Location Level</b> is a number assigned to each security that represents whether you want the security to be located in a qualified account or a taxable account. This only applies if you are rebalancing at the household level, with portfolios containing both taxable and qualified accounts. " +
            "When a rebalance runs, it will place the securities with the highest absolute value numbers into the desired accounts first, and place those with numbers nearest to zero last. The result is a greater chance of getting the securities marked as -100 into taxable accounts and closest to +100 into qualified accounts. "+
            "The <b>Taxable Alternate</b> is a security that will be bought when rebalancing if the subject security is placed in the wrong (or not desired) account type. "+
            "If an alternate security is selected, you will also have to specify the <b>Account type</b> in which you want to buy the alternate security.</p>";
        return msg;
    }
    locationLevel() {
        let msg = "<div>The asset location <b>level</b> is a number assigned to each security that represents your relative preference for each security to be purchased in either " +
            "a qualified account or a taxable account. +100 represents the highest tax impact, and therefore the greatest preference for qualified account placement, " +
            "while -100 represents the lowest tax impact, and therefore the greatest preference for taxable account placement.</div>";

        msg += "<br /><div>During rebalancing, iRebal will identify securities with the highest absolute value of location <b>level</b> and will attempt to place those securities " +
            "into the desired accounts first. Securities marked closer to -100 and +100 will have a greater likelihood of being placed into the appropriate taxable and qualified accounts. " +
            "Note: When locating into a qualified account, <b>level</b> does not make a distinction between qualified and Roth accounts.</div>";

        msg +="<br/><div>If a security is already held in the portfolio and you prefer that subsequent purchases of the security are placed in the account where it is already held, enter a location level of between -49 to +49 for the security.</div>";
        return msg;
    }
    locationRothLevel() {
        let msg = "<div>The <strong>Roth level</strong> is a number  assigned to securities identifying a preference to be bought in a Roth account. Numbers may range from +100 to 0, with +100 representing greatest preference to locate into a Roth account relative to other securities with a lower <b>Roth level</b>.</div>";

        msg += "<br /><div>During rebalancing, securities with a defined <b>Roth level</b> have the highest priority of placement, and will be located first based on the securities with the highest <b>Roth level</b> value. In absence of a defined <b>Roth level</b>, iRebal will locate securities based on location <b>level</b>, and will not make a distinction between qualified and Roth accounts.</div>";
        return msg;
    }
    minAlt() {
        return '<b>Min Alt</b> is your optional selection of a similar substitute security to buy if a trade smaller than the Min Buy amount is proposed. Any holdings of the Min Alt will be treated as an equivalent.';
    }
    equivs() {
        let msg = "<b>Equivalents</b> are securities that, when held, will be treated as an acceptable substitute for the parent security. ";
        msg += "Holdings in equivalents are counted toward the allocation of the parent security when calculating a portfolio's allocations compared to ";
        msg += "target. Equivalent securities will not be purchased, but can be held and sold in place of the parent security.";
        return msg;
    }
    altAfHeld() {
        let msg = "<p><b>Alt if Held</b> securities are those you'd like iRebal to purchase instead of a specific security when the <b>Alt if Held</b> ";
        msg += "is already in the account. If you specify multiple alternates, iRebal will purchase whichever one there's more of in the account.</p>";
        msg += "<p><b>Example:</b> An alternate might be specified in the case of a fund that's closed to new investors: Since subsequent future purchases  ";
        msg += "of the closed fund are sometimes allowed, the <b>Alt if Held</b> fund will be purchased when the fund is held; the new fund will only be  ";
        msg += "purchased for initial investors.</p>";
        return msg;
    }
    altSec() {
        return 'These settings tell iRebal to purchase an alternate security which will be used in place of this security when trading in a certain account type. For example, if the subject security is a corporate bond fund, and you\'d like to use a municipal bond fund in its place in taxable accounts, enter the symbol for the municipal bond fund in the Alt Sec field and select Taxable as the Alt Acct.';
    }
    altAcct() {
        return 'These settings tell iRebal to purchase an alternate security which will be used in place of this security when trading in a certain account type. For example, if the subject security is a corporate bond fund, and you\'d like to use a municipal bond fund in its place in taxable accounts, enter the symbol for the municipal bond fund in the Alt Sec field and select Taxable as the Alt Acct.';
    }
    tlhWashSaleAlternate() {
        return 'Wash sale alternate.';
    }

    getDefaultHiddenState(parent: SecurityAllocationsComponent, fieldName: string, defaultState: boolean) {
        let s: any = JSON.parse(window.localStorage.getItem(parent.componentName) || "{}");

        if(s.COL_STATE) {
            let c: any  = JSON.parse(s.COL_STATE);

            if(c) {
                let d = c.find((x: any) => x.colId == fieldName);
                return d?.hide;
            }
        }

        return defaultState;
    }
}
